import { PropTypes } from 'prop-types';
import FJLocalStore from '../../../store/FJLocalStore';
import classNames from 'classnames';
import FJToolTip from '../FJToolTip/FJToolTip';
import { useRef, useCallback, useMemo } from 'react';
import FJPreviewBox from '../FJPreviewBox/FJPreviewBox';
import DeleteIcon from '../../../assets/svg/delete-white.svg?react';
import UploadCircleIcon from '../../../assets/svg/upload-circle.svg?react';
import DragReplaceIcon from '../../../assets/svg/drag-replace.svg?react';
import FJImageInput from '../FJImageInput/FJImageInput';
import FJTitleLine from '../../combineComponents/FJTitleLine/FJTitleLine';
import FJHorizontalScrollArrow from '../FJTab/FJHorizontalScrollArrow.jsx';
import styles from './FJMultipleImageUploader.module.less';
import { useEffect } from 'react';
import FJLoading from '../FJLoading/FJLoading';

/**
 * FJMultipleImageUploader - 多图片上传组件
 *
 * 功能说明：
 * 这是一个通用的多图片上传组件，支持以下功能：
 * 1. 多图片上传：支持通过点击、拖拽或媒体库选择多个图片文件
 * 2. 图片预览：以横向滚动列表的形式展示已上传的图片
 * 3. 图片管理：支持删除单个图片，显示上传进度和数量限制
 * 4. 交互优化：支持横向滚动浏览，自动滚动到最新上传的图片
 * 5. 状态管理：支持禁用状态、拖拽状态等UI反馈
 *
 * 使用场景：
 * - 其他需要多图片上传功能的场景
 */
const FJMultipleImageUploader = ({
    inputImages = [],
    onUploadMultiple,
    onRemoveImage,
    isDraggingFile,
    setIsDraggingFile,
    acceptFile,
    openMediaLibrary,
    disable = false,
    maxImages,
    renderTitle,
    titleStyle,
    isLoading = false,
    isRequire = false,
    previewImageStyle,
}) => {
    // const isDraggingFile = true;
    const inputRef = useRef(null);
    const scrollArrowRef = useRef(null);
    const preShowImages = useRef([]);
    const showImages = useMemo(() => {
        return inputImages.slice(0, maxImages);
    }, [inputImages, maxImages]);
    const hasImages = showImages.length > 0;

    const reachMaxImages = useMemo(() => {
        return showImages.length >= maxImages;
    }, [showImages, maxImages]);
    useEffect(() => {
        if (showImages.length > preShowImages.current.length && scrollArrowRef.current?.scroller) {
            scrollArrowRef.current?.scroller.scrollToLeft(999, 'smooth');
        }
        preShowImages.current = showImages;
    }, [showImages]);

    const handleFileSelect = useCallback(
        files => {
            if (disable) return;
            if (files.length > 0) {
                onUploadMultiple(files);
            }
            // 需要清空 input 的 value， 避免选同一个图片不会触发
            inputRef.current.value = '';
        },
        [disable, onUploadMultiple],
    );

    const handleUpload = useCallback(
        e => {
            e.stopPropagation();
            if (disable || reachMaxImages) return;

            if (window.isEditPage && openMediaLibrary) {
                openMediaLibrary();
            } else if (inputRef.current) {
                inputRef.current.click();
            }
        },
        [disable, openMediaLibrary, reachMaxImages],
    );

    const handleRemoveImage = useCallback(
        (index, e) => {
            e.stopPropagation();
            if (disable || isLoading) return;
            onRemoveImage(index);
        },
        [disable, onRemoveImage],
    );

    const handleDragOver = useCallback(
        e => {
            if (disable || isLoading) return;
            e.stopPropagation();
            e.preventDefault();
            setIsDraggingFile(true);
        },
        [setIsDraggingFile],
    );

    const handleDragLeave = useCallback(
        e => {
            if (disable || isLoading) return;
            e.stopPropagation();
            e.preventDefault();
            setIsDraggingFile(false);
        },
        [disable, setIsDraggingFile],
    );

    const handleDragDrop = useCallback(
        e => {
            if (disable || isLoading) return;
            e.stopPropagation();
            e.preventDefault();
            setIsDraggingFile(false);

            const files = Array.from(e.dataTransfer.files);
            if (files && files.length > 0) {
                onUploadMultiple(files);
            }
        },
        [disable, setIsDraggingFile, onUploadMultiple],
    );

    const _handleImageClick = useCallback(e => {
        e.stopPropagation();
        e.preventDefault();
        const rect = e.target.getBoundingClientRect();
        if (rect.left < 100) {
            scrollArrowRef.current && scrollArrowRef.current.moveScroller('left', rect.left);
        } else if (rect.left > 385) {
            scrollArrowRef.current && scrollArrowRef.current.moveScroller('right', rect.right);
        }
    }, []);

    return (
        <>
            <FJTitleLine
                title={FJLocalStore._('upload')}
                renderTitle={renderTitle}
                isShowHelp={false}
                isShowTry={false}
                style={titleStyle}
                isRequire={isRequire}
                renderRight={() => {
                    return (
                        <div className={styles.upload_num_text}>
                            {showImages.length}/{maxImages}
                        </div>
                    );
                }}
            />
            <div className={styles.container}>
                <div
                    className={classNames(styles.upload_area, {
                        [styles.dragging]: isDraggingFile,
                        [styles.disabled]: disable,
                    })}
                    onDragOver={handleDragOver}
                    onDrop={handleDragDrop}
                    onDragLeave={handleDragLeave}
                >
                    {hasImages ? (
                        <div className={styles.images_area}>
                            {
                                <div
                                    className={classNames(styles.add_image_item, {
                                        [styles.disabled]: reachMaxImages,
                                    })}
                                    onClick={handleUpload}
                                >
                                    <div className={styles.upload_border_box}></div>
                                    <div className={styles.add_image_box}>
                                        <UploadCircleIcon className={styles.add_icon} />
                                    </div>
                                </div>
                            }
                            <FJHorizontalScrollArrow
                                ref={scrollArrowRef}
                                className={styles.image_list_box}
                                leftButtonStyle={{ left: -8 }}
                                rightButtonStyle={{ right: -8 }}
                                updateList={showImages}
                            >
                                <div className={styles.images}>
                                    {showImages?.map((image, index) => (
                                        <div key={index} className={styles.image_item} onClick={_handleImageClick}>
                                            <div className={styles.image_preview}>
                                                <img
                                                    src={image.src}
                                                    alt={image.name || 'image'}
                                                    className={styles.image}
                                                    loading='lazy'
                                                />
                                                <FJPreviewBox
                                                    imageInfo={image}
                                                    className={styles.preview_mask}
                                                    style={previewImageStyle}
                                                />
                                            </div>
                                            <FJToolTip title={FJLocalStore._('delete')}>
                                                <div
                                                    className={classNames(styles.delete_btn, {
                                                        [styles.disabled]: disable,
                                                    })}
                                                    onClick={e => handleRemoveImage(index, e)}
                                                >
                                                    <DeleteIcon className={styles.delete_icon} />
                                                </div>
                                            </FJToolTip>
                                        </div>
                                    ))}
                                </div>
                            </FJHorizontalScrollArrow>
                        </div>
                    ) : (
                        <FJImageInput
                            isDraggingFile={isDraggingFile}
                            setIsDraggingFile={setIsDraggingFile}
                            acceptFile={acceptFile}
                            isMultiple={true}
                            openMediaLibrary={openMediaLibrary}
                            uploadImg={handleFileSelect}
                            uploadText={FJLocalStore._('upload-up-to-xxx-images').replace('xxx', maxImages)}
                        />
                    )}

                    {isDraggingFile && hasImages && (
                        <div className={classNames(styles.drag_mask, { [styles.dragging]: isDraggingFile })}>
                            <DragReplaceIcon />
                            <span className={styles.drag_text}>{FJLocalStore._('drag-and-drop')}</span>
                        </div>
                    )}

                    {isLoading && (
                        <div className={styles.loading}>
                            <div className={styles.loading_mask}>
                                <FJLoading strokeColor='#a8acbc' />
                            </div>
                        </div>
                    )}
                    <input
                        type='file'
                        ref={inputRef}
                        className={styles.file_input}
                        accept={acceptFile}
                        multiple
                        onChange={e => handleFileSelect(Array.from(e.target.files))}
                    />
                </div>
            </div>
        </>
    );
};

FJMultipleImageUploader.propTypes = {
    inputImages: PropTypes.array,
    uploadStatus: PropTypes.string,
    onUploadMultiple: PropTypes.func.isRequired,
    onRemoveImage: PropTypes.func.isRequired,
    isDraggingFile: PropTypes.bool,
    setIsDraggingFile: PropTypes.func,
    acceptFile: PropTypes.string,
    openMediaLibrary: PropTypes.func,
    maxImages: PropTypes.number,
    disable: PropTypes.bool,
    renderTitle: PropTypes.func,
    titleStyle: PropTypes.object,
    isLoading: PropTypes.bool,
    required: PropTypes.bool,
};

export default FJMultipleImageUploader;
